import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Upload, X, Palette } from "lucide-react";
import { CAROUSEL_TEMPLATES } from "@/lib/carouselTemplates";

const FONT_PAIRS = [
  { id: "playfair-inter", heading: "Playfair Display, serif",   body: "Inter, system-ui, sans-serif", label: "Playfair · Inter (editorial)" },
  { id: "georgia-inter",  heading: "Georgia, 'Times New Roman', serif", body: "Inter, system-ui, sans-serif", label: "Georgia · Inter (classic)" },
  { id: "inter-inter",    heading: "Inter, system-ui, sans-serif", body: "Inter, system-ui, sans-serif", label: "Inter · Inter (modern minimal)" },
  { id: "system-system",  heading: "-apple-system, BlinkMacSystemFont, sans-serif", body: "-apple-system, BlinkMacSystemFont, sans-serif", label: "System native (safest)" },
];

export default function BrandKit() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [kit, setKit] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/brand-kit");
        if (!cancel) setKit(data.brand_kit);
      } catch (e) {
        toast.error("Could not load your brand kit");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/brand-kit", patch);
      setKit(data.brand_kit);
      toast.success("Brand kit saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const set = (patch) => setKit((k) => ({ ...(k || {}), ...patch }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API}/files/${data.file_id}`;
      await save({ logo_url: url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#1D4E89]" />
      </div>
    );
  }

  const pickFont = FONT_PAIRS.find(f => f.heading === kit.font_heading && f.body === kit.font_body)?.id || "playfair-inter";

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-10" data-testid="brand-kit-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <Link to="/settings" className="dms-link inline-flex items-center gap-2 text-[13px]">
            <ArrowLeft className="w-4 h-4" /> Back to settings
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold mt-3 text-[#0A2342] flex items-center gap-3">
            <Palette className="w-8 h-8 text-[#1D4E89]" /> Brand Kit
          </h1>
          <p className="text-[14.5px] text-[#64748B] mt-2 max-w-2xl">
            Set this once. Every carousel, slide export and brand-aware output will follow it — colours, typography and logo — so your output stays visually consistent across every post.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-6">
          {/* Colours */}
          <section className="dms-card p-5 sm:p-7">
            <h2 className="dms-h2 mb-1">Colours</h2>
            <p className="text-[13px] text-[#64748B] mb-6">Your primary is used for backgrounds and headlines. Accent is used for CTAs and highlights.</p>
            <div className="grid sm:grid-cols-3 gap-4">
              <ColorInput label="Primary"   testid="color-primary"   value={kit.primary_color}   onChange={(v) => set({ primary_color: v })} />
              <ColorInput label="Secondary" testid="color-secondary" value={kit.secondary_color} onChange={(v) => set({ secondary_color: v })} />
              <ColorInput label="Accent"    testid="color-accent"    value={kit.accent_color}    onChange={(v) => set({ accent_color: v })} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <ColorInput label="Text on primary"  testid="color-text-primary" value={kit.text_on_primary} onChange={(v) => set({ text_on_primary: v })} />
              <ColorInput label="Text on light"    testid="color-text-light"   value={kit.text_on_light}   onChange={(v) => set({ text_on_light: v })} />
            </div>
          </section>

          {/* Typography */}
          <section className="dms-card p-5 sm:p-7">
            <h2 className="dms-h2 mb-1">Typography</h2>
            <p className="text-[13px] text-[#64748B] mb-6">Pick a font pair. Headings use the display font; body text uses the sans-serif for readability.</p>
            <div className="space-y-2">
              {FONT_PAIRS.map((f) => (
                <label key={f.id} data-testid={`font-pair-${f.id}`} className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition ${pickFont === f.id ? "border-[#1D4E89] bg-[#F0F7FF]" : "border-[#E2E8F0] hover:border-[#94A3B8]"}`}>
                  <input
                    type="radio" name="font-pair" checked={pickFont === f.id}
                    onChange={() => set({ font_heading: f.heading, font_body: f.body })}
                    className="w-4 h-4"
                  />
                  <div className="flex-1">
                    <div className="text-[13px] font-medium text-[#0A2342]">{f.label}</div>
                    <div style={{ fontFamily: f.heading, fontSize: 20, marginTop: 4 }}>Dive deeper into the story.</div>
                    <div style={{ fontFamily: f.body, fontSize: 13, color: "#64748B", marginTop: 2 }}>A calm, professional look built for underwater brands.</div>
                  </div>
                </label>
              ))}
            </div>
          </section>

          {/* Logo */}
          <section className="dms-card p-5 sm:p-7">
            <h2 className="dms-h2 mb-1">Logo</h2>
            <p className="text-[13px] text-[#64748B] mb-4">Upload a PNG or SVG with a transparent background. It will sit unobtrusively in the corner of every slide.</p>

            {kit.logo_url ? (
              <div className="flex items-center gap-4 p-4 bg-[#F8FAFC] rounded-lg">
                <img src={kit.logo_url} alt="Brand logo" className="h-16 w-auto max-w-[180px] object-contain" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-[#0A2342] truncate">{kit.logo_url.split("/").pop()}</div>
                </div>
                <button
                  data-testid="logo-remove"
                  onClick={() => save({ logo_url: null })}
                  className="text-[#B91C1C] hover:bg-[#FEF2F2] p-2 rounded"
                  aria-label="Remove logo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label data-testid="logo-upload-btn" className={`inline-flex items-center gap-2 px-4 py-3 border-2 border-dashed border-[#CBD5E1] rounded-lg cursor-pointer hover:border-[#1D4E89] hover:bg-[#F0F7FF] transition ${uploading ? "opacity-60" : ""}`}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <span className="text-[13.5px] font-medium text-[#0A2342]">{uploading ? "Uploading…" : "Upload logo"}</span>
                <input
                  type="file" accept="image/png,image/svg+xml,image/webp" className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])}
                />
              </label>
            )}

            <div className="mt-6">
              <label className="dms-label">Position on slide</label>
              <select
                data-testid="logo-position"
                value={kit.logo_position || "bottom-left"}
                onChange={(e) => set({ logo_position: e.target.value })}
                className="dms-input mt-2"
              >
                <option value="none">Hide logo</option>
                <option value="bottom-left">Bottom left</option>
                <option value="bottom-right">Bottom right</option>
                <option value="top-left">Top left</option>
                <option value="top-right">Top right</option>
              </select>
            </div>
          </section>

          {/* Default template */}
          <section className="dms-card p-5 sm:p-7">
            <h2 className="dms-h2 mb-1">Default carousel template</h2>
            <p className="text-[13px] text-[#64748B] mb-6">This is the design your slides will follow. You can still switch templates on the slide builder itself for any specific carousel.</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {CAROUSEL_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  data-testid={`template-${t.id}`}
                  onClick={() => set({ active_template_id: t.id })}
                  className={`text-left border rounded-lg overflow-hidden transition ${kit.active_template_id === t.id ? "border-[#1D4E89] ring-2 ring-[#1D4E89]/20" : "border-[#E2E8F0] hover:border-[#94A3B8]"}`}
                >
                  <div style={{ background: t.thumbnail.bg, height: 100 }} className="flex items-center justify-center">
                    <span style={{ color: t.thumbnail.accent, fontSize: 12, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase" }}>
                      {t.name}
                    </span>
                  </div>
                  <div className="p-2 text-[12.5px] font-medium text-[#0A2342]">{t.name}</div>
                </button>
              ))}
            </div>
          </section>

          {/* Save bar */}
          <div className="sticky bottom-0 pt-2">
            <div className="dms-card p-3 sm:p-4 flex items-center justify-between shadow-lg">
              <div className="text-[13px] text-[#64748B]">
                Changes are staged locally. Click <strong>Save</strong> to apply to future outputs.
              </div>
              <button
                data-testid="brand-kit-save"
                onClick={() => save(kit)}
                disabled={saving}
                className="dms-btn-primary"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save brand kit
              </button>
            </div>
          </div>
        </div>

        {/* Live preview */}
        <aside className="lg:sticky lg:top-6 space-y-3">
          <div className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#64748B]">Preview</div>
          <div className="dms-card p-3">
            <MiniPreview brand={kit} templateId={kit.active_template_id} />
          </div>
          <div className="text-[12px] text-[#64748B]">
            This mini-preview updates as you tweak. Full slide builder is one step below the carousel generator.
          </div>
        </aside>
      </div>
    </div>
  );
}

function ColorInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="dms-label">{label}</label>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="color"
          data-testid={`${testid}-picker`}
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded border border-[#E2E8F0] cursor-pointer p-1"
        />
        <input
          type="text"
          data-testid={`${testid}-text`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#0A2342"
          className="dms-input flex-1 font-mono text-[13px] uppercase"
        />
      </div>
    </div>
  );
}

// Small preview showing one cover + one body slide side-by-side.
function MiniPreview({ brand, templateId }) {
  // We inline a lightweight preview instead of loading the full renderer to
  // keep the settings page snappy. The full builder shows exact-res previews.
  const t = CAROUSEL_TEMPLATES.find(x => x.id === templateId) || CAROUSEL_TEMPLATES[0];
  const bg = t.background({ ...brand });
  const heading = brand.font_heading || "Playfair Display, serif";
  return (
    <div style={{ background: bg, aspectRatio: "1080/1350", borderRadius: 8, position: "relative", overflow: "hidden", padding: 24, color: /^(#0|#1|linear-gradient\(.*#0)/i.test(bg) ? "#fff" : "#0A2342" }}>
      <div style={{ width: 24, height: 3, background: brand.accent_color || "#F59E0B", marginBottom: 20 }} />
      <div style={{ fontFamily: heading, fontSize: 26, fontWeight: 700, lineHeight: 1.05, letterSpacing: "-0.02em" }}>
        Your headline appears here.
      </div>
      <div style={{ fontSize: 11, marginTop: 12, opacity: 0.7 }}>Body preview text will use your paired sans.</div>
      {brand.logo_url && (
        <img src={brand.logo_url} alt="" crossOrigin="anonymous" style={{ position: "absolute", bottom: 12, left: 12, height: 22, opacity: 0.9 }} />
      )}
    </div>
  );
}
